import { useEffect, useState } from 'react';

type Pos = 'tl' | 'tr' | 'bl' | 'br';

export function HudCorner({
  pos,
  label,
  sub,
}: {
  pos: Pos;
  label?: string;
  sub?: string;
}) {
  const isTop = pos.includes('t');
  const isLeft = pos.includes('l');
  const cornerStyle: React.CSSProperties = {
    position: 'absolute',
    [isTop ? 'top' : 'bottom']: 18,
    [isLeft ? 'left' : 'right']: 18,
    width: 60,
    height: 60,
    pointerEvents: 'none',
  };
  const horiz: React.CSSProperties = {
    position: 'absolute',
    background: '#66e0ff',
    width: 28,
    height: 1.5,
    opacity: 0.85,
    [isTop ? 'top' : 'bottom']: 0,
    [isLeft ? 'left' : 'right']: 0,
  };
  const vert: React.CSSProperties = {
    position: 'absolute',
    background: '#66e0ff',
    width: 1.5,
    height: 28,
    opacity: 0.85,
    [isTop ? 'top' : 'bottom']: 0,
    [isLeft ? 'left' : 'right']: 0,
  };
  return (
    <div style={cornerStyle}>
      <span style={horiz} />
      <span style={vert} />
      {label && (
        <div
          style={{
            position: 'absolute',
            [isTop ? 'top' : 'bottom']: 6,
            [isLeft ? 'left' : 'right']: 36,
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: 9,
            letterSpacing: '0.16em',
            color: 'rgba(102, 224, 255, 0.85)',
            textTransform: 'uppercase',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
          {sub && (
            <div
              style={{
                color: 'rgba(255,255,255,0.45)',
                marginTop: 2,
                fontSize: 8.5,
              }}
            >
              {sub}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const TONE_COLORS = {
  ok: '#3ddc97',
  warn: '#f5b441',
  err: '#ff5e5e',
  info: '#66e0ff',
} as const;
type Tone = keyof typeof TONE_COLORS;

export function StatusDot({ tone = 'ok' }: { tone?: Tone }) {
  const c = TONE_COLORS[tone];
  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <span
        style={{
          display: 'inline-block',
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: c,
          boxShadow: `0 0 8px ${c}`,
        }}
      />
      <span
        style={{
          position: 'absolute',
          inset: -3,
          borderRadius: '50%',
          background: c,
          opacity: 0.3,
          animation: 'dd-pulse-soft 2s ease-in-out infinite',
        }}
      />
    </span>
  );
}

export function StatusFooter({ compact = false }: { compact?: boolean }) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const build = (import.meta.env.VITE_APP_VERSION as string | undefined) ??
    '2.4.0+a3f9d';
  const items: Array<{ dot?: Tone; label?: string; val: string }> = [
    { dot: 'ok', label: 'all systems', val: '99.94%' },
    { label: 'region', val: 'sa-east-1' },
    { label: 'build', val: build },
    { label: 'sync', val: `${((tick * 0.7) % 100).toFixed(1)}%` },
    { label: 'lat', val: '12ms' },
  ];
  return (
    <div
      style={{
        position: 'absolute',
        bottom: compact ? 12 : 22,
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: 18,
        padding: '8px 18px',
        background: 'rgba(10, 15, 18, 0.7)',
        border: '1px solid rgba(102, 224, 255, 0.15)',
        borderRadius: 6,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        fontFamily: 'JetBrains Mono, monospace',
        fontSize: 10,
        letterSpacing: '0.08em',
        color: 'rgba(255,255,255,0.55)',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        zIndex: 5,
      }}
    >
      {items.map((it, i) => (
        <span
          key={i}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          {i > 0 && <span style={{ opacity: 0.3, marginRight: 6 }}>·</span>}
          {it.dot && <StatusDot tone={it.dot} />}
          {it.label && <span style={{ opacity: 0.6 }}>{it.label}</span>}
          <span style={{ color: '#66e0ff' }}>{it.val}</span>
        </span>
      ))}
    </div>
  );
}
